import React from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  Text,
  Drawer,
  DrawerContent,
  useColorModeValue,
  BoxProps,
  useBreakpointValue,
  Image
} from '@chakra-ui/react';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiServer,
  FiMonitor,
  FiMapPin,
  FiCreditCard,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@/stores/authStore';

interface NavItemProps extends BoxProps {
  icon: IconType;
  to: string;
  children: React.ReactNode;
}

interface SidebarProps {
  role?: UserRole;
  isOpen: boolean;
  onClose: () => void;
}

interface LinkItemProps {
  name: string;
  translationKey: string;
  icon: IconType;
  to: string;
  roles: UserRole[];
}

const LinkItems: Array<LinkItemProps> = [
  { name: 'Dashboard', translationKey: 'navigation.dashboard', icon: FiHome, to: '/', roles: ['SYSTEM', 'TENANT_ADMIN'] },
  { name: 'Users', translationKey: 'navigation.users', icon: FiUsers, to: '/users', roles: ['SYSTEM'] },
  { name: 'Tenants', translationKey: 'navigation.tenants', icon: FiServer, to: '/tenants', roles: ['SYSTEM'] },
  { name: 'Devices', translationKey: 'navigation.devices', icon: FiMonitor, to: '/devices', roles: ['SYSTEM', 'TENANT_ADMIN'] },
  { name: 'Device Logs', translationKey: 'navigation.deviceLogs', icon: FiMonitor, to: '/device-logs', roles: ['SYSTEM'] },
  { name: 'Parkings', translationKey: 'navigation.parkings', icon: FiMapPin, to: '/parkings', roles: ['SYSTEM', 'TENANT_ADMIN'] },
  { name: 'Payments', translationKey: 'navigation.payments', icon: FiCreditCard, to: '/payments', roles: ['SYSTEM', 'TENANT_ADMIN'] },
  { name: 'MQTT UI', translationKey: 'navigation.mqttUI', icon: FiServer, to: '/mqtt', roles: ['SYSTEM'] },
  { name: 'System Health', translationKey: 'navigation.systemHealth', icon: FiServer, to: '/system', roles: ['SYSTEM'] },
];

type NavLinkRenderProps = { isActive: boolean };
  // Parameters<NonNullable<React.ComponentProps<typeof RouterLink>['children']>>[0];

const NavItem = ({ icon, to, children, ...rest }: NavItemProps) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  
  return (
    <Box
      as={RouterLink}
      to={to}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}
    >
      {({ isActive }: NavLinkRenderProps) => (
        <Flex
          align="center"
          p="4"
          mx="4"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          bg={isActive ? 'rgb(248, 171, 22, 0.7)' : 'transparent'}
          color={isActive ? 'white' : textColor}
          _hover={{
            bg: isActive ? 'rgb(248, 171, 22, 0.3)' : hoverBg,
          }}
          {...rest}
        >
          {icon && (
            <Icon
              mr="4"
              fontSize="16"
              as={icon}
            />
          )}
          {children}
        </Flex>
      )}
    </Box>
  );
};

const SidebarContent = ({ role, onClose, isOpen, ...rest }: SidebarProps & BoxProps) => {
  const { t } = useTranslation();
  
  return (
    <Box
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        {/* <Text fontSize="2xl" fontWeight="bold">
          LetsParky
        </Text> */}
        <Image 
          src="/logo.png" 
          alt="LetsParky Logo" 
          width={10}
          height={10}
          borderRadius="6px"
        />
        <Text fontSize="2xl" ml={2} fontWeight="bold">LetsParky</Text>
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {LinkItems.filter(link => !role || link.roles.includes(role)).map((link) => (
        <NavItem key={link.name} icon={link.icon} to={link.to}>
          {t(link.translationKey)}
        </NavItem>
      ))}
    </Box>
  );
};

const Sidebar = ({ isOpen, onClose, role }: SidebarProps) => {
  const isDesktop = useBreakpointValue({ base: false, md: true });

  if (isDesktop) {
    return (
      <SidebarContent
        role={role}
        onClose={onClose}
        isOpen={isOpen}
        display={{ base: 'none', md: 'block' }}
      />
    );
  }

  return (
    <Drawer
      autoFocus={false}
      isOpen={isOpen}
      placement="left"
      onClose={onClose}
      returnFocusOnClose={false}
      onOverlayClick={onClose}
      size="full"
    >
      <DrawerContent>
        <SidebarContent role={role} onClose={onClose} isOpen={isOpen} />
      </DrawerContent>
    </Drawer>
  );
};

export default Sidebar; 